// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import Latex from './Latex.js';
import Figure from './Figure.js';
import { illustrationColor } from './designSystem.js';
import { Language, English, Spanish } from './languages.js';
import { LatexTextAtom } from './LatexTextAtom.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';

const VPADDING = 10;
const HPADDING = 10;
const HPADDING_EDGE = 15;

export function TwoColumnProof(props: {|
  children: ChildrenArray<Element<typeof ProofGoal | typeof ProofRow>>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    TwoColumnProof,
  );

  const childrenArray = React.Children.toArray(props.children);
  const proofGoal = childrenArray[0];
  const proofRows = childrenArray.slice(1);

  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    if (proofGoal.type.name !== 'ProofGoal') {
      const elName = String(proofGoal.type.name);
      addErrors([
        {
          message: `The first child must be <ProofGoal> not <${elName}>`,
          ...proofGoal.props._sourceLocations?.[elName],
        },
      ]);
      // Subsequent error messages are confusing if this check has failed as well
      // so we we'll bail-out early here.
      return;
    }

    if (proofRows.length === 0) {
      addErrors([
        {
          message:
            'There must be 1 or more <ProofRow> following the <ProofGoal>',
          ...locations.TwoColumnProof,
        },
      ]);
    }

    const nonProofRow = proofRows.find((row) => row.type.name !== 'ProofRow');
    if (nonProofRow !== undefined) {
      const elName = String(nonProofRow.type.name);
      addErrors([
        {
          message: 'All children after <ProofGoal> must be <ProofRow>',
          ...nonProofRow.props._sourceLocations?.[elName],
        },
      ]);
    }
  });

  if (failedValidation) return null;
  return (
    <Figure>
      <div
        style={{
          background: illustrationColor.snuff,
          borderRadius: 4,
          overflow: 'hidden', // Clip to border radius
          paddingBottom: VPADDING,
        }}
      >
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <caption
            style={{
              padding: `${VPADDING}px ${HPADDING_EDGE}px`,
              fontWeight: 'bold',
            }}
          >
            {proofGoal}
          </caption>
          <colgroup>
            <col span="1" style={{ width: '6%' }} />
            <col span="1" style={{ width: '47%' }} />
            <col span="1" style={{ width: '47%' }} />
          </colgroup>
          <thead
            style={{
              background: illustrationColor.governorBay,
              color: 'white',
              textAlign: 'left',
            }}
          >
            <tr>
              <th />
              <th style={{ padding: `5px ${HPADDING}px` }}>
                <Language>
                  <English>Statements</English>
                  <Spanish>Los enunciados</Spanish>
                </Language>
              </th>
              <th
                style={{ padding: `5px ${HPADDING_EDGE}px 5px ${HPADDING}px` }}
              >
                <Language>
                  <English>Reasons</English>
                  <Spanish>Las razones</Spanish>
                </Language>
              </th>
            </tr>
          </thead>
          <tbody>
            {proofRows.map((row, i) =>
              // $FlowIgnore can't figure this one out as React$CloneElement implementation is a special type
              React.cloneElement(row, { _rowLabel: `${i + 1}.` }),
            )}
          </tbody>
        </table>
      </div>
    </Figure>
  );
}
TwoColumnProof.spec = {
  children: t.childrenArray(
    t.unionOfTypes(t.elementType(ProofGoal), t.elementType(ProofRow)),
  ),
};

export function ProofGoal(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, ProofGoal);
  if (failedValidation) return null;
  return (
    <>
      <Language>
        <English>To prove</English>
        <Spanish>Probar</Spanish>
      </Language>
      : {props.children}
    </>
  );
}
ProofGoal.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

const TD_STYLE = { padding: `10px ${HPADDING}px 0 ${HPADDING}px` };

export function ProofRow(props: {|
  _rowLabel: string,
  children: [Element<typeof ProofStatement>, Element<typeof ProofReason>],
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, ProofRow);
  if (failedValidation) return null;
  return (
    <tr>
      <td
        style={{ ...TD_STYLE, paddingLeft: HPADDING_EDGE, fontWeight: 'bold' }}
      >
        {props._rowLabel}
      </td>
      {props.children}
    </tr>
  );
}
ProofRow.spec = {
  children: t.tuple([
    t.elementType(ProofStatement),
    t.elementType(ProofReason),
  ]),
};

export function ProofStatement(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, ProofStatement);
  if (failedValidation) return null;
  return <td style={TD_STYLE}>{props.children}</td>;
}
ProofStatement.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function ProofReason(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, ProofReason);
  if (failedValidation) return null;
  return (
    <td style={{ ...TD_STYLE, paddingRight: HPADDING_EDGE }}>
      {props.children}
    </td>
  );
}
ProofReason.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};
